/**
 * useHierarchy Hook
 *
 * Manages ICS hierarchy state, including sector info, escalations, and broadcasts.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import type { Libp2p } from 'libp2p'
import type {
  HierarchyRoomType,
  HierarchyRole,
  SectorInfo,
  EscalatedReport,
  CommandBroadcast,
  GeoLocation,
  Message,
  EscalationPriority,
} from '@/lib/types'
import {
  SECTOR_HEARTBEAT_INTERVAL_MS,
  SECTOR_OFFLINE_THRESHOLD_HEARTBEATS,
} from '@/lib/types'
import {
  getRoomDocument,
  getHierarchyMeta,
  getRoomMeta,
  initializeHierarchyMeta,
  addSectorToHierarchy,
  removeSectorFromHierarchy,
  addEscalation,
  getUnacknowledgedEscalations,
  acknowledgeEscalation as ackEscalationCRDT,
  addBroadcast,
  getBroadcasts,
  updateSectorInfo,
  getSectorInfos,
  updateSectorStatus,
  updateSectorBoundary as updateSectorBoundaryCRDT,
  updateSectorCentroid,
  assignOrganizerToSector as assignOrganizerCRDT,
  addOrganizerAssignment,
  addRemoteCommand,
  type RoomDocument,
  type EscalatedReportEntry,
  type CommandBroadcastEntry,
  type SectorInfoEntry,
} from '@/lib/sync'
import {
  publishEscalation,
  publishBroadcast,
  publishSectorStatus,
  publishOrganizerAssignment,
  publishRemoteCommand,
  publishBoundaryUpdate,
  subscribeToAllHierarchyTopics,
  subscribeToHierarchyTopicsForSector,
} from '@/lib/p2p'
import {
  createEscalatedReport,
  serializeEscalatedReport,
  deserializeEscalatedReport,
} from '@/lib/hierarchy/escalation'
import {
  createCommandBroadcast,
  serializeCommandBroadcast,
  deserializeCommandBroadcast,
  createBroadcastTextMessage,
} from '@/lib/hierarchy/broadcast'
import {
  createOrganizerAssignment,
  serializeOrganizerAssignment,
  deserializeOrganizerAssignment,
  toOrganizerAssignmentEntry,
} from '@/lib/hierarchy/organizer-assignment'
import {
  createRemoteCommand,
  serializeRemoteCommand,
  deserializeRemoteCommand,
  toRemoteCommandEntry,
  type RemoteCommandPayload,
  type RemoteCommand,
} from '@/lib/hierarchy/commands'
import {
  isGeofenceContained,
  suggestBoundaryAdjustment,
  type GeofenceBoundary,
  type AdjustmentResult,
} from '@/lib/geo'
import { generateRoomSecret } from '@/lib/crypto'
import type { RoomIdentity } from './useIdentity'

export interface UseHierarchyOptions {
  roomId: string
  commandRoomId?: string // Set for sector rooms
  node: Libp2p | null
  identity: RoomIdentity | null
  hierarchyType: HierarchyRoomType
  commandBoundary?: GeofenceBoundary // Command room boundary for validation
}

/** Extended sector info with boundary data */
export interface ExtendedSectorInfo extends SectorInfo {
  centerLat?: number
  centerLng?: number
  radiusMeters?: number
  organizerPeerId?: string
  organizerPublicKey?: string
  participantCentroidLat?: number
  participantCentroidLng?: number
  sectorSecret?: string
}

export interface UseHierarchyReturn {
  // State
  isHierarchyRoom: boolean
  hierarchyType: HierarchyRoomType
  hierarchyRole: HierarchyRole
  commandRoomId: string | null
  sectorRoomIds: string[]
  sectors: ExtendedSectorInfo[]
  pendingEscalations: EscalatedReport[]
  recentBroadcasts: CommandBroadcast[]
  boundaryAdjustmentSuggestion: AdjustmentResult | null
  pendingRemoteCommands: RemoteCommand[]

  // Command room actions
  initializeAsCommand: (commanderPublicKey: string) => void
  addSector: (sectorRoomId: string, sectorName: string, center: GeoLocation, radiusMeters: number, leadPeerId?: string, leadName?: string) => { success: boolean; error?: string; sectorSecret?: string }
  removeSector: (sectorRoomId: string) => void
  getSectorSecret: (sectorRoomId: string) => string | undefined
  broadcastToSectors: (message: string, targetRooms: string[] | 'all', priority: EscalationPriority) => Promise<void>
  acknowledgeEscalation: (escalationId: string) => void

  // New command room actions
  assignOrganizerToSector: (sectorRoomId: string, participantPeerId: string, participantPublicKey: string) => Promise<void>
  updateSectorBoundary: (sectorRoomId: string, newCenter: GeoLocation, newRadius: number) => Promise<{ success: boolean; error?: string }>
  sendRemoteCommand: (sectorRoomId: string, command: RemoteCommandPayload) => Promise<void>

  // Sector room actions
  initializeAsSector: (parentCommandRoomId: string) => void
  escalateMessage: (message: Message, priority: EscalationPriority) => Promise<void>
  sendSectorHeartbeat: (participantCount: number, location?: GeoLocation, status?: 'active' | 'alert' | 'moving') => Promise<void>

  // Boundary adjustment helpers
  checkBoundaryContainment: (sectorBoundary: GeofenceBoundary) => { isContained: boolean; maxAllowedRadius?: number }
  updateParticipantCentroid: (sectorRoomId: string, participantLocations: GeoLocation[]) => void
}

export function useHierarchy(options: UseHierarchyOptions): UseHierarchyReturn {
  const { roomId, commandRoomId: parentCommandRoomId, node, identity, hierarchyType, commandBoundary } = options

  const [roomDoc, setRoomDoc] = useState<RoomDocument | null>(null)
  const [hierarchyRole, setHierarchyRole] = useState<HierarchyRole>('team_member')
  const [sectorRoomIds, setSectorRoomIds] = useState<string[]>([])
  const [sectors, setSectors] = useState<ExtendedSectorInfo[]>([])
  const [pendingEscalations, setPendingEscalations] = useState<EscalatedReport[]>([])
  const [recentBroadcasts, setRecentBroadcasts] = useState<CommandBroadcast[]>([])
  const [boundaryAdjustmentSuggestion, setBoundaryAdjustmentSuggestion] = useState<AdjustmentResult | null>(null)
  const [pendingRemoteCommands, setPendingRemoteCommands] = useState<RemoteCommand[]>([])

  const heartbeatIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const lastHeartbeatRef = useRef<Map<string, number>>(new Map())

  // Get the command room ID (either this room or parent)
  const commandRoomId = hierarchyType === 'command' ? roomId : parentCommandRoomId ?? null

  // Get command boundary from room meta if not provided
  const [actualCommandBoundary, setActualCommandBoundary] = useState<GeofenceBoundary | null>(commandBoundary ?? null)

  // Initialize room document
  useEffect(() => {
    if (!roomId) return

    const doc = getRoomDocument(roomId)
    setRoomDoc(doc)

    // Load initial hierarchy state
    const meta = getHierarchyMeta(doc)
    if (meta) {
      setSectorRoomIds(meta.childRoomIds)
    }

    // Load room meta for command boundary (if command room)
    if (hierarchyType === 'command' && !commandBoundary) {
      const roomMeta = getRoomMeta(doc)
      if (roomMeta) {
        setActualCommandBoundary({
          center: roomMeta.center,
          radiusMeters: roomMeta.radiusMeters,
        })
      }
    }

    // Load sectors, escalations, broadcasts
    setSectors(convertSectorInfoEntriesToExtended(getSectorInfos(doc)))
    setPendingEscalations(convertEscalationEntries(getUnacknowledgedEscalations(doc)))
    setRecentBroadcasts(convertBroadcastEntries(getBroadcasts(doc, 20)))

    // Determine role based on identity and room type
    if (identity) {
      if (hierarchyType === 'command') {
        // In command room, check if we're the commander
        if (identity.role === 'organizer') {
          setHierarchyRole('commander')
        } else if (identity.role === 'coordinator') {
          setHierarchyRole('deputy')
        } else {
          setHierarchyRole('team_member')
        }
      } else if (hierarchyType === 'sector') {
        // In sector room
        if (identity.role === 'organizer') {
          setHierarchyRole('sector_lead')
        } else {
          setHierarchyRole('team_member')
        }
      }
    }
  }, [roomId, identity, hierarchyType, commandBoundary])

  // Subscribe to hierarchy P2P topics
  useEffect(() => {
    if (!node || !commandRoomId) return

    let unsubscribe: (() => void) | undefined

    if (hierarchyType === 'command') {
      // Command room subscribes to all hierarchy topics
      unsubscribe = subscribeToAllHierarchyTopics(node, commandRoomId, {
        onEscalation: async (data) => {
          try {
            const report = deserializeEscalatedReport(data)
            // Add to local CRDT
            if (roomDoc) {
              addEscalation(roomDoc, convertToEscalationEntry(report))
            }
            // Update state
            setPendingEscalations((prev) => {
              if (prev.find((e) => e.id === report.id)) return prev
              return [...prev, report]
            })
          } catch (err) {
            console.error('[useHierarchy] Failed to handle escalation:', err)
          }
        },
        onSectorStatus: async (data) => {
          try {
            const statusUpdate = JSON.parse(new TextDecoder().decode(data))
            if (roomDoc) {
              updateSectorStatus(roomDoc, statusUpdate.sectorRoomId, statusUpdate.status, statusUpdate.participantCount)
            }
            // Update last heartbeat time
            lastHeartbeatRef.current.set(statusUpdate.sectorRoomId, Date.now())
            // Update sectors state
            setSectors((prev) => {
              const existing = prev.find((s) => s.roomId === statusUpdate.sectorRoomId)
              if (existing) {
                return prev.map((s) =>
                  s.roomId === statusUpdate.sectorRoomId
                    ? { ...s, status: statusUpdate.status, participantCount: statusUpdate.participantCount, lastActivity: new Date() }
                    : s
                )
              }
              return prev
            })
          } catch (err) {
            console.error('[useHierarchy] Failed to handle sector status:', err)
          }
        },
      })

      // Start offline detection interval
      const offlineCheckInterval = setInterval(() => {
        const now = Date.now()
        const offlineThreshold = SECTOR_HEARTBEAT_INTERVAL_MS * SECTOR_OFFLINE_THRESHOLD_HEARTBEATS

        setSectors((prev) =>
          prev.map((sector) => {
            const lastHb = lastHeartbeatRef.current.get(sector.roomId) ?? 0
            if (now - lastHb > offlineThreshold && sector.status !== 'offline') {
              return { ...sector, status: 'offline' }
            }
            return sector
          })
        )
      }, SECTOR_HEARTBEAT_INTERVAL_MS)

      return () => {
        unsubscribe?.()
        clearInterval(offlineCheckInterval)
      }
    } else if (hierarchyType === 'sector') {
      // Sector room subscribes to broadcasts, assignments, and commands
      unsubscribe = subscribeToHierarchyTopicsForSector(node, commandRoomId, {
        onBroadcast: async (data) => {
          try {
            const broadcast = deserializeCommandBroadcast(data)
            // Check if this broadcast is for us
            if (broadcast.targetRoomIds === 'all' || broadcast.targetRoomIds.includes(roomId)) {
              // Add to local CRDT
              if (roomDoc) {
                addBroadcast(roomDoc, convertToBroadcastEntry(broadcast))
              }
              // Update state
              setRecentBroadcasts((prev) => {
                if (prev.find((b) => b.id === broadcast.id)) return prev
                return [broadcast, ...prev].slice(0, 20)
              })
            }
          } catch (err) {
            console.error('[useHierarchy] Failed to handle broadcast:', err)
          }
        },
        onOrganizerAssignment: async (data) => {
          try {
            const assignment = deserializeOrganizerAssignment(data)
            // Check if this assignment is for our sector
            if (assignment.sectorRoomId === roomId) {
              console.log('[useHierarchy] Received organizer assignment for this sector:', assignment)
              // The sector room will handle the role change through the identity system
              // For now, just log it - the assignment verification happens via the signature
            }
          } catch (err) {
            console.error('[useHierarchy] Failed to handle organizer assignment:', err)
          }
        },
        onRemoteCommand: async (data) => {
          try {
            const remoteCommand = deserializeRemoteCommand(data)
            // Check if this command is for our sector
            if (remoteCommand.sectorRoomId === roomId) {
              console.log('[useHierarchy] Received remote command:', remoteCommand)
              // Add to pending commands for handling
              setPendingRemoteCommands((prev) => {
                if (prev.find((c) => c.id === remoteCommand.id)) return prev
                return [...prev, remoteCommand]
              })
            }
          } catch (err) {
            console.error('[useHierarchy] Failed to handle remote command:', err)
          }
        },
        onBoundaryUpdate: async (data) => {
          try {
            const boundaryUpdate = JSON.parse(new TextDecoder().decode(data))
            // Check if this update is for our sector
            if (boundaryUpdate.sectorRoomId === roomId) {
              console.log('[useHierarchy] Received boundary update:', boundaryUpdate)
              // Boundary updates will be processed through the room's meta update system
            }
          } catch (err) {
            console.error('[useHierarchy] Failed to handle boundary update:', err)
          }
        },
      })

      // Start heartbeat interval
      heartbeatIntervalRef.current = setInterval(() => {
        // This will be called by the component using the hook
        // The component should call sendSectorHeartbeat periodically
      }, SECTOR_HEARTBEAT_INTERVAL_MS)

      return () => {
        unsubscribe?.()
        if (heartbeatIntervalRef.current) {
          clearInterval(heartbeatIntervalRef.current)
        }
      }
    }
  }, [node, commandRoomId, hierarchyType, roomId, roomDoc])

  // Command room actions
  const initializeAsCommand = useCallback(
    (commanderPublicKey: string) => {
      if (!roomDoc) return
      initializeHierarchyMeta(roomDoc, 'command', undefined, commanderPublicKey)
    },
    [roomDoc]
  )

  const addSector = useCallback(
    (sectorRoomId: string, sectorName: string, center: GeoLocation, radiusMeters: number, leadPeerId?: string, leadName?: string): { success: boolean; error?: string; sectorSecret?: string } => {
      if (!roomDoc) return { success: false, error: 'Room document not initialized' }

      // Validate boundary containment if we have command boundary
      if (actualCommandBoundary) {
        const sectorBoundary: GeofenceBoundary = { center, radiusMeters }
        const containment = isGeofenceContained(sectorBoundary, actualCommandBoundary)
        if (!containment.isContained) {
          return {
            success: false,
            error: `Sector boundary extends beyond command boundary. Maximum allowed radius at this location: ${Math.round(containment.maxAllowedRadius ?? 0)}m`,
          }
        }
      }

      // Generate a room secret for the sector
      const sectorSecret = generateRoomSecret()

      addSectorToHierarchy(roomDoc, sectorRoomId)
      setSectorRoomIds((prev) => [...prev, sectorRoomId])

      const sectorInfo: SectorInfoEntry = {
        roomId: sectorRoomId,
        name: sectorName,
        participantCount: 0,
        status: 'active',
        lastActivity: Date.now(),
        pendingEscalations: 0,
        leadPeerId,
        leadName,
        // Boundary data
        centerLat: center.latitude,
        centerLng: center.longitude,
        radiusMeters,
        // Store sector secret for sharing
        sectorSecret,
      }
      updateSectorInfo(roomDoc, sectorInfo)
      setSectors((prev) => [...prev, convertSectorInfoEntryToExtended(sectorInfo)])

      console.log('[useHierarchy] Created sector:', sectorRoomId, 'with secret')
      return { success: true, sectorSecret }
    },
    [roomDoc, actualCommandBoundary]
  )

  const removeSector = useCallback(
    (sectorRoomId: string) => {
      if (!roomDoc) return

      removeSectorFromHierarchy(roomDoc, sectorRoomId)
      setSectorRoomIds((prev) => prev.filter((id) => id !== sectorRoomId))
      setSectors((prev) => prev.filter((s) => s.roomId !== sectorRoomId))
      console.log('[useHierarchy] Removed sector:', sectorRoomId)
    },
    [roomDoc]
  )

  const getSectorSecret = useCallback(
    (sectorRoomId: string): string | undefined => {
      if (!roomDoc) return undefined
      const sectorInfos = getSectorInfos(roomDoc)
      const sector = sectorInfos.find((s) => s.roomId === sectorRoomId)
      return sector?.sectorSecret
    },
    [roomDoc]
  )

  const broadcastToSectors = useCallback(
    async (message: string, targetRooms: string[] | 'all', priority: EscalationPriority) => {
      if (!node || !identity || !commandRoomId) return

      const textMessage = createBroadcastTextMessage(
        message,
        identity.peerId,
        identity.displayName
      )

      const broadcast = await createCommandBroadcast(
        commandRoomId,
        targetRooms,
        textMessage,
        identity.peerId,
        identity.displayName,
        priority,
        identity.privateKey
      )

      // Publish to hierarchy topic
      const serialized = serializeCommandBroadcast(broadcast)
      await publishBroadcast(node, commandRoomId, serialized)

      // Also store in local CRDT
      if (roomDoc) {
        addBroadcast(roomDoc, convertToBroadcastEntry(broadcast))
      }

      // Update local state
      setRecentBroadcasts((prev) => [broadcast, ...prev].slice(0, 20))
    },
    [node, identity, commandRoomId, roomDoc]
  )

  const acknowledgeEscalation = useCallback(
    (escalationId: string) => {
      if (!roomDoc || !identity) return

      ackEscalationCRDT(roomDoc, escalationId, identity.peerId)
      setPendingEscalations((prev) => prev.filter((e) => e.id !== escalationId))
    },
    [roomDoc, identity]
  )

  // New command room actions
  const assignOrganizerToSector = useCallback(
    async (sectorRoomId: string, participantPeerId: string, participantPublicKey: string) => {
      if (!node || !identity || !commandRoomId || !roomDoc) return

      // Create the assignment
      const assignment = await createOrganizerAssignment(
        commandRoomId,
        sectorRoomId,
        participantPeerId,
        participantPublicKey,
        identity.peerId,
        identity.displayName,
        identity.privateKey,
        identity.publicKey
      )

      // Publish to P2P topic
      const serialized = serializeOrganizerAssignment(assignment)
      await publishOrganizerAssignment(node, commandRoomId, serialized)

      // Store in CRDT
      addOrganizerAssignment(roomDoc, toOrganizerAssignmentEntry(assignment))

      // Update sector info in CRDT
      assignOrganizerCRDT(roomDoc, sectorRoomId, participantPeerId, participantPublicKey)

      // Update local state
      setSectors((prev) =>
        prev.map((s) =>
          s.roomId === sectorRoomId
            ? { ...s, organizerPeerId: participantPeerId, organizerPublicKey: participantPublicKey, leadPeerId: participantPeerId }
            : s
        )
      )
    },
    [node, identity, commandRoomId, roomDoc]
  )

  const updateSectorBoundary = useCallback(
    async (sectorRoomId: string, newCenter: GeoLocation, newRadius: number): Promise<{ success: boolean; error?: string }> => {
      if (!node || !identity || !commandRoomId || !roomDoc) {
        return { success: false, error: 'Not initialized' }
      }

      // Validate boundary containment
      if (actualCommandBoundary) {
        const sectorBoundary: GeofenceBoundary = { center: newCenter, radiusMeters: newRadius }
        const containment = isGeofenceContained(sectorBoundary, actualCommandBoundary)
        if (!containment.isContained) {
          return {
            success: false,
            error: `Sector boundary extends beyond command boundary. Maximum allowed radius: ${Math.round(containment.maxAllowedRadius ?? 0)}m`,
          }
        }
      }

      // Create and send remote command for boundary update
      const command = await createRemoteCommand(
        commandRoomId,
        sectorRoomId,
        { type: 'update_boundary', centerLat: newCenter.latitude, centerLng: newCenter.longitude, radiusMeters: newRadius },
        identity.peerId,
        identity.displayName,
        identity.privateKey,
        identity.publicKey
      )

      const serialized = serializeRemoteCommand(command)
      await publishRemoteCommand(node, commandRoomId, serialized)

      // Also publish dedicated boundary update
      const boundaryUpdate = {
        sectorRoomId,
        centerLat: newCenter.latitude,
        centerLng: newCenter.longitude,
        radiusMeters: newRadius,
        updatedBy: identity.peerId,
        timestamp: Date.now(),
      }
      await publishBoundaryUpdate(node, commandRoomId, new TextEncoder().encode(JSON.stringify(boundaryUpdate)))

      // Update CRDT
      updateSectorBoundaryCRDT(roomDoc, sectorRoomId, newCenter.latitude, newCenter.longitude, newRadius)

      // Update local state
      setSectors((prev) =>
        prev.map((s) =>
          s.roomId === sectorRoomId
            ? { ...s, centerLat: newCenter.latitude, centerLng: newCenter.longitude, radiusMeters: newRadius }
            : s
        )
      )

      return { success: true }
    },
    [node, identity, commandRoomId, roomDoc, actualCommandBoundary]
  )

  const sendRemoteCommand = useCallback(
    async (sectorRoomId: string, command: RemoteCommandPayload) => {
      if (!node || !identity || !commandRoomId || !roomDoc) return

      const remoteCommand = await createRemoteCommand(
        commandRoomId,
        sectorRoomId,
        command,
        identity.peerId,
        identity.displayName,
        identity.privateKey,
        identity.publicKey
      )

      // Publish to P2P topic
      const serialized = serializeRemoteCommand(remoteCommand)
      await publishRemoteCommand(node, commandRoomId, serialized)

      // Store in CRDT
      addRemoteCommand(roomDoc, toRemoteCommandEntry(remoteCommand))
    },
    [node, identity, commandRoomId, roomDoc]
  )

  // Boundary helpers
  const checkBoundaryContainment = useCallback(
    (sectorBoundary: GeofenceBoundary): { isContained: boolean; maxAllowedRadius?: number } => {
      if (!actualCommandBoundary) {
        return { isContained: true }
      }
      const containment = isGeofenceContained(sectorBoundary, actualCommandBoundary)
      return {
        isContained: containment.isContained,
        maxAllowedRadius: containment.maxAllowedRadius,
      }
    },
    [actualCommandBoundary]
  )

  const updateParticipantCentroid = useCallback(
    (sectorRoomId: string, participantLocations: GeoLocation[]) => {
      if (!roomDoc || participantLocations.length === 0) return

      const suggestion = suggestBoundaryAdjustment(
        { center: { latitude: 0, longitude: 0 }, radiusMeters: 100 }, // Will be overwritten
        participantLocations
      )

      if (suggestion.suggestedCenter) {
        updateSectorCentroid(
          roomDoc,
          sectorRoomId,
          suggestion.suggestedCenter.latitude,
          suggestion.suggestedCenter.longitude
        )

        // Update local state
        setSectors((prev) =>
          prev.map((s) =>
            s.roomId === sectorRoomId
              ? {
                  ...s,
                  participantCentroidLat: suggestion.suggestedCenter!.latitude,
                  participantCentroidLng: suggestion.suggestedCenter!.longitude,
                }
              : s
          )
        )

        // Check if we should suggest boundary adjustment
        const currentSector = sectors.find((s) => s.roomId === sectorRoomId)
        if (currentSector?.centerLat !== undefined && currentSector?.centerLng !== undefined && currentSector?.radiusMeters) {
          const sectorBoundary: GeofenceBoundary = {
            center: { latitude: currentSector.centerLat, longitude: currentSector.centerLng },
            radiusMeters: currentSector.radiusMeters,
          }
          const adjustmentSuggestion = suggestBoundaryAdjustment(sectorBoundary, participantLocations)
          if (adjustmentSuggestion.needsAdjustment) {
            setBoundaryAdjustmentSuggestion(adjustmentSuggestion)
          } else {
            setBoundaryAdjustmentSuggestion(null)
          }
        }
      }
    },
    [roomDoc, sectors]
  )

  // Sector room actions
  const initializeAsSector = useCallback(
    (parentCommandRoomId: string) => {
      if (!roomDoc) return
      initializeHierarchyMeta(roomDoc, 'sector', parentCommandRoomId)
    },
    [roomDoc]
  )

  const escalateMessage = useCallback(
    async (message: Message, priority: EscalationPriority) => {
      if (!node || !identity || !commandRoomId || !roomDoc) return

      const roomMeta = getHierarchyMeta(roomDoc)
      const sectorName = roomMeta?.type === 'sector' ? 'Sector' : 'Room'

      const report = await createEscalatedReport(
        message,
        roomId,
        sectorName,
        identity.peerId,
        identity.displayName,
        priority,
        identity.privateKey
      )

      // Publish to hierarchy topic
      const serialized = serializeEscalatedReport(report)
      await publishEscalation(node, commandRoomId, serialized)
    },
    [node, identity, commandRoomId, roomId, roomDoc]
  )

  const sendSectorHeartbeat = useCallback(
    async (participantCount: number, location?: GeoLocation, status: 'active' | 'alert' | 'moving' = 'active') => {
      if (!node || !commandRoomId) return

      const statusUpdate = {
        sectorRoomId: roomId,
        status,
        participantCount,
        locationLat: location?.latitude,
        locationLng: location?.longitude,
        timestamp: Date.now(),
      }

      const serialized = new TextEncoder().encode(JSON.stringify(statusUpdate))
      await publishSectorStatus(node, commandRoomId, serialized)
    },
    [node, commandRoomId, roomId]
  )

  return {
    isHierarchyRoom: hierarchyType !== 'standalone',
    hierarchyType,
    hierarchyRole,
    commandRoomId,
    sectorRoomIds,
    sectors,
    pendingEscalations,
    recentBroadcasts,
    boundaryAdjustmentSuggestion,
    pendingRemoteCommands,

    // Command room actions
    initializeAsCommand,
    addSector,
    removeSector,
    getSectorSecret,
    broadcastToSectors,
    acknowledgeEscalation,

    // New command room actions
    assignOrganizerToSector,
    updateSectorBoundary,
    sendRemoteCommand,

    // Sector room actions
    initializeAsSector,
    escalateMessage,
    sendSectorHeartbeat,

    // Boundary adjustment helpers
    checkBoundaryContainment,
    updateParticipantCentroid,
  }
}

// ============= Conversion utilities =============

function convertSectorInfoEntriesToExtended(entries: SectorInfoEntry[]): ExtendedSectorInfo[] {
  return entries.map(convertSectorInfoEntryToExtended)
}

function convertSectorInfoEntryToExtended(entry: SectorInfoEntry): ExtendedSectorInfo {
  return {
    roomId: entry.roomId,
    name: entry.name,
    participantCount: entry.participantCount,
    location: entry.locationLat !== undefined && entry.locationLng !== undefined
      ? { latitude: entry.locationLat, longitude: entry.locationLng }
      : { latitude: 0, longitude: 0 },
    leadPeerId: entry.leadPeerId,
    leadName: entry.leadName,
    status: entry.status as 'active' | 'offline' | 'alert',
    lastActivity: new Date(entry.lastActivity),
    pendingEscalations: entry.pendingEscalations,
    // Extended fields
    centerLat: entry.centerLat,
    centerLng: entry.centerLng,
    radiusMeters: entry.radiusMeters,
    organizerPeerId: entry.organizerPeerId,
    organizerPublicKey: entry.organizerPublicKey,
    participantCentroidLat: entry.participantCentroidLat,
    participantCentroidLng: entry.participantCentroidLng,
    sectorSecret: entry.sectorSecret,
  }
}

function convertEscalationEntries(entries: EscalatedReportEntry[]): EscalatedReport[] {
  return entries.map((entry) => ({
    type: 'escalated_report' as const,
    id: entry.id,
    sourceRoomId: entry.sourceRoomId,
    sourceRoomName: entry.sourceRoomName,
    originalMessage: {
      id: entry.originalMessageId,
      type: entry.originalMessageType,
      senderId: '',
      senderName: '',
      senderRole: 'participant' as const,
      timestamp: new Date(entry.timestamp),
      content: entry.originalMessageContent,
    } as Message,
    escalatedBy: entry.escalatedBy,
    escalatedByName: entry.escalatedByName,
    priority: entry.priority,
    timestamp: new Date(entry.timestamp),
    signature: entry.signature,
    acknowledged: entry.acknowledged,
    acknowledgedBy: entry.acknowledgedBy,
    acknowledgedAt: entry.acknowledgedAt ? new Date(entry.acknowledgedAt) : undefined,
  }))
}

function convertBroadcastEntries(entries: CommandBroadcastEntry[]): CommandBroadcast[] {
  return entries.map((entry) => ({
    type: 'command_broadcast' as const,
    id: entry.id,
    commandRoomId: entry.commandRoomId,
    targetRoomIds: entry.targetRoomIds,
    message: {
      id: entry.messageId,
      type: 'text' as const,
      senderId: entry.broadcastBy,
      senderName: entry.broadcastByName,
      senderRole: 'organizer' as const, // Commanders are organizers in the room role system
      timestamp: new Date(entry.timestamp),
      content: entry.messageContent,
    },
    broadcastBy: entry.broadcastBy,
    broadcastByName: entry.broadcastByName,
    priority: entry.priority,
    timestamp: new Date(entry.timestamp),
    signature: entry.signature,
  }))
}

function convertToEscalationEntry(report: EscalatedReport): EscalatedReportEntry {
  return {
    id: report.id,
    sourceRoomId: report.sourceRoomId,
    sourceRoomName: report.sourceRoomName,
    originalMessageId: report.originalMessage.id,
    originalMessageContent: (report.originalMessage as { content?: string }).content,
    originalMessageType: report.originalMessage.type as 'text' | 'voice' | 'poi',
    escalatedBy: report.escalatedBy,
    escalatedByName: report.escalatedByName,
    priority: report.priority,
    timestamp: report.timestamp instanceof Date ? report.timestamp.getTime() : new Date(report.timestamp).getTime(),
    signature: report.signature,
    acknowledged: report.acknowledged ?? false,
    acknowledgedBy: report.acknowledgedBy,
    acknowledgedAt: report.acknowledgedAt instanceof Date ? report.acknowledgedAt.getTime() : undefined,
  }
}

function convertToBroadcastEntry(broadcast: CommandBroadcast): CommandBroadcastEntry {
  return {
    id: broadcast.id,
    commandRoomId: broadcast.commandRoomId,
    targetRoomIds: broadcast.targetRoomIds,
    messageId: broadcast.message.id,
    messageContent: (broadcast.message as { content?: string }).content ?? '',
    messageType: broadcast.message.type as 'text' | 'voice',
    broadcastBy: broadcast.broadcastBy,
    broadcastByName: broadcast.broadcastByName,
    priority: broadcast.priority,
    timestamp: broadcast.timestamp instanceof Date ? broadcast.timestamp.getTime() : new Date(broadcast.timestamp).getTime(),
    signature: broadcast.signature,
  }
}

export default useHierarchy
